import { revalidateLogic } from "@tanstack/react-form";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

import { useAppForm } from "@/components/app-form";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal";
import { Button } from "@/components/ui/button";
import { ErrorModal } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { useShop } from "@/hooks/use-shop";
import { applyCouponCode } from "@/features/coupons/apply-coupon";
import { AudioSlot } from "@/features/audio/kiosk-audio";

export const couponSchema = z.object({
  coupon: z.string().min(4),
});

/**
 * Typing in a coupon code. A valid one is stored for the session (the next
 * payment is created with it) and `onApplied` runs once its details were seen.
 * Shared by the coupon screen and the payment flow.
 */
export function CouponCodeForm({
  machineId,
  dryerBundleId,
  showTitle = true,
  onApplied,
}: {
  machineId?: number;
  dryerBundleId?: number;
  // the payment flow's header already says "Van kuponod?"
  showTitle?: boolean;
  onApplied: () => void;
}) {
  const { showModal, closeModal } = useMuiModal();
  const { t } = useTranslation();
  const { data: shop } = useShop();
  const machineNames: Record<string, string> = {
    mosogep: t("Mosógép"),
    ozon: t("Ózon"),
    szaritogep: t("Szárítógép"),
  };

  const form = useAppForm({
    defaultValues: { coupon: "" },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: couponSchema,
      onSubmitAsync: async ({ value }) => {
        const response = await applyCouponCode({
          code: value.coupon,
          machineId,
          dryerBundleId,
          source: "manual",
        });

        if (response.type === "error") {
          showModal(
            <ErrorModal
              errorKey={response.error}
              translatedMessage={response.translatedMessage}
            />,
          );
          return;
        }

        if (response.type === "success" && response.data) {
          const data = response.data;
          showModal(
            <Modal>
              <AudioSlot slot="modal.coupon-valid" />
              <ModalTitle>
                <Trans>Érvényes kuponkód</Trans>
              </ModalTitle>
              <ModalContent>
                <p>
                  <Trans>Engedmény mértéke:</Trans>{" "}
                  {data.fixDiscount
                    ? formatCurrency(data.fixDiscount, shop.currencyCode)
                    : data.percentage + "%"}
                </p>
                <p>
                  <Trans>Felhasználható: </Trans>
                  <span>
                    {data.machines
                      .map((key: string) => machineNames[key] ?? key)
                      .join(", ")}
                  </span>
                </p>
                <Button
                  className={`font-helvetica mt-8`}
                  onClick={() => {
                    closeModal();
                    onApplied();
                  }}
                >
                  <Trans>Ok</Trans>
                </Button>
              </ModalContent>
            </Modal>,
          );
        }
      },
    },
  });

  return (
    <>
      {showTitle && (
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-8`}>
          <Trans>Van kuponod?</Trans>
        </h2>
      )}
      <p
        className={`flex gap-x-1 items-center text-2xl text-center justify-center mb-12`}
      >
        <img src={`/arrow-attention.svg`} className={`size-8`} />
        <Trans>
          Add meg a kuponkódodat, és azonnal érvényesítjük a kedvezményt!
        </Trans>
      </p>
      <form.AppForm>
        <form
          className="space-y-12"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField
            name="coupon"
            children={(field) => (
              <field.TextField
                tabIndex={1}
                type="username"
                label={t("Kuponkód")}
                uppercase
                disableHorizontalGrowth
                className={`[&_input]:w-xl [&_input]:py-10 [&_input]:text-5xl [&_label]:text-center [&_label]:text-3xl grid justify-center gap-4`}
              />
            )}
          />

          <div className="mt-20 grid gap-10 items-center justify-center">
            <form.Subscribe
              selector={(state) => [state.values, state.isSubmitting] as const}
              children={([values, isSubmitting]) => (
                <Button
                  className={`font-bold w-xl py-7 text-4xl px-0`}
                  type="submit"
                  disabled={
                    !couponSchema.safeParse(values).success || isSubmitting
                  }
                >
                  <Trans>Megadom</Trans>
                </Button>
              )}
            />
          </div>
        </form>
      </form.AppForm>
    </>
  );
}

export function CouponHelpText() {
  return (
    <Trans>
      Itt tudod beváltani kuponodat. A beviteli mezőben add meg a kupon kódját,
      majd az "Ok" gombra kattintva váltsd be a kedvezményed. A gomb megnyomása
      előtt ellenőrizd a kuponkód helyességét.
    </Trans>
  );
}
